import utils from "../../../utils/utils";
import { getVolunteerDisplayName, getVolunteerRowId } from "./participantService";

export const PM_ACTIONS_COLUMN = "rowActions";
export const PM_PINNED_COLUMNS = [PM_ACTIONS_COLUMN];

export const PM_VOL_STATUS_OPTIONS = [
  { value: 0, label: "Active" },
  { value: 1, label: "Inactive" },
  { value: 2, label: "Removed" },
];

const PM_STATUS_PILL_STYLES = {
  active: { background: "#dcfce7", color: "#166534" },
  inactive: { background: "#fef3c7", color: "#92400e" },
  withdrawn: { background: "#fef3c7", color: "#92400e" },
  removed: { background: "#fee2e2", color: "#991b1b" },
  archived: { background: "#fee2e2", color: "#991b1b" },
  "screen failed": { background: "#fee2e2", color: "#991b1b" },
  default: { background: "#e2e8f0", color: "#334155" },
};

export function getVolunteerStatusLabel(statusOrCode) {
  if (typeof statusOrCode === "number" || /^\d+$/.test(String(statusOrCode ?? "").trim())) {
    const code = Number(statusOrCode);
    return (
      PM_VOL_STATUS_OPTIONS.find((option) => option.value === code)?.label || "—"
    );
  }
  const text = String(statusOrCode ?? "").trim();
  if (!text) return "—";
  if (text.toLowerCase() === "archived") return "Removed";
  return text;
}

export function getVolunteerStatusPillStyle(statusOrCode) {
  const label = getVolunteerStatusLabel(statusOrCode).toLowerCase();
  return PM_STATUS_PILL_STYLES[label] || PM_STATUS_PILL_STYLES.default;
}

const PM_PARTICIPANT_DB_PILL_STYLES = {
  "in participant db": { background: "#e0f2fe", color: "#0369a1" },
  "not in participant db": { background: "#f1f5f9", color: "#475569" },
  // Legacy label keys (kept so older filter values still style correctly).
  "on list": { background: "#e0f2fe", color: "#0369a1" },
  "not listed": { background: "#f1f5f9", color: "#475569" },
  default: { background: "#e2e8f0", color: "#334155" },
};

/** True when volunteer is flagged for the Participant Database. */
export function isParticipantOnDb(value) {
  const key = String(value ?? "")
    .trim()
    .toLowerCase();
  return (
    key === "yes" ||
    key === "y" ||
    key === "1" ||
    key === "true" ||
    key === "on list" ||
    key === "in participant db"
  );
}

/** Map Yes/No (or 1/0) from IsUseVolunteerDB to display labels. */
export function getParticipantDbLabel(value) {
  const raw = String(value ?? "").trim();
  if (!raw || raw === "—") return "—";
  if (isParticipantOnDb(raw)) return "In Participant DB";
  const key = raw.toLowerCase();
  if (
    key === "no" ||
    key === "n" ||
    key === "0" ||
    key === "false" ||
    key === "not listed" ||
    key === "not in participant db"
  ) {
    return "Not in Participant DB";
  }
  return raw;
}

export function getParticipantDbPillStyle(value) {
  const label = getParticipantDbLabel(value).toLowerCase();
  return PM_PARTICIPANT_DB_PILL_STYLES[label] || PM_PARTICIPANT_DB_PILL_STYLES.default;
}

export const PM_NAME_CELL_MAX_LEN = 24;
export const PM_EMAIL_CELL_MAX_LEN = 22;

export function truncateParticipantText(text, maxLen) {
  const value = String(text ?? "").trim() || "—";
  if (value === "—" || value.length <= maxLen) {
    return { value, display: value };
  }
  return { value, display: `${value.slice(0, maxLen)}…` };
}

export const PM_DOB_DATE_RANGE_INITIAL = {
  preset: "All Time",
  startDate: "",
  endDate: "",
};

/** Client filter: any date/datetime value within DateRangeFilter ISO start/end (inclusive). */
export function matchesPmDateRange(value, dateRange) {
  if (
    !dateRange ||
    dateRange.preset === "All Time" ||
    (!dateRange.startDate && !dateRange.endDate)
  ) {
    return true;
  }

  const raw = String(value ?? "").trim();
  if (!raw || raw === "—" || raw.toUpperCase() === "N/A") return false;

  const date = utils.parseDateInput(raw);
  if (!date) return false;

  const startRaw = dateRange.startDate
    ? utils.parseDateInput(dateRange.startDate)
    : null;
  const endRaw = dateRange.endDate
    ? utils.parseDateInput(dateRange.endDate)
    : null;

  if (startRaw) {
    const from = new Date(
      startRaw.getFullYear(),
      startRaw.getMonth(),
      startRaw.getDate(),
      0,
      0,
      0,
      0,
    );
    if (date < from) return false;
  }
  if (endRaw) {
    const to = new Date(
      endRaw.getFullYear(),
      endRaw.getMonth(),
      endRaw.getDate(),
      23,
      59,
      59,
      999,
    );
    if (date > to) return false;
  }
  return true;
}

/** Client filter: DOB within DateRangeFilter ISO start/end (inclusive). */
export function matchesParticipantDobRange(row, dateRange) {
  return matchesPmDateRange(row?.DOB, dateRange);
}

/** Multi-select status filter; empty selection = all. */
export function matchesParticipantStatusFilter(row, selectedLabels) {
  if (!Array.isArray(selectedLabels) || selectedLabels.length === 0) return true;
  const label = getVolunteerStatusLabel(row?.VolStatus);
  return selectedLabels.some(
    (selected) =>
      String(selected).trim().toLowerCase() === label.toLowerCase(),
  );
}

/** Composite column: avatar + name + gender */
export const PM_PARTICIPANT_COLUMN = "Participant";
/** Composite column: email + phone */
export const PM_CONTACT_COLUMN = "Contact";

export const ACTIVE_PARTICIPANT_FIELD_OPTIONS = [
  { key: PM_PARTICIPANT_COLUMN, label: "Participant" },
  { key: "VolunteerNo", label: "Participant Id" },
  { key: "DOB", label: "Date Of Birth" },
  { key: PM_CONTACT_COLUMN, label: "Contact" },
  { key: "IsUseVolunteerDB", label: "Participant DB" },
  { key: "VolStatus", label: "Status" },
  { key: PM_ACTIONS_COLUMN, label: "Actions" },
];

export const DEFAULT_ACTIVE_PARTICIPANT_COLUMNS = [
  PM_PARTICIPANT_COLUMN,
  "VolunteerNo",
  "DOB",
  PM_CONTACT_COLUMN,
  "IsUseVolunteerDB",
  "VolStatus",
  PM_ACTIONS_COLUMN,
];

export const INACTIVE_PARTICIPANT_FIELD_OPTIONS = [
  { key: "FirstName", label: "First Name" },
  { key: "LastName", label: "Last Name" },
  { key: "VolunteerNo", label: "Participant ID" },
  { key: "DOB", label: "Date of Birth" },
  { key: "Gender", label: "Gender" },
  { key: "ContactNumber", label: "Contact" },
  { key: "EmailId", label: "Email" },
  { key: "ArchivedOn", label: "Archived On" },
  { key: "VolStatus", label: "Status" },
  { key: PM_ACTIONS_COLUMN, label: "Actions" },
];

export const DEFAULT_INACTIVE_PARTICIPANT_COLUMNS = [
  "FirstName",
  "LastName",
  "VolunteerNo",
  "DOB",
  "Gender",
  "ContactNumber",
  "EmailId",
  "ArchivedOn",
  "VolStatus",
  PM_ACTIONS_COLUMN,
];

export const CAMPAIGN_TEMPLATE_FIELD_OPTIONS = [
  { key: "TemplateName", label: "Template Name" },
  { key: "NumberOfTimeUserd", label: "Number of Times Used" },
  { key: "Source", label: "Source" },
  { key: "MostRecentUsed", label: "Most Recently Used" },
  { key: "TemplateType", label: "Type" },
  { key: "CampaignStatus", label: "Status" },
];

export const DEFAULT_CAMPAIGN_TEMPLATE_COLUMNS = [
  "TemplateName",
  "NumberOfTimeUserd",
  "Source",
  "MostRecentUsed",
  "TemplateType",
  "CampaignStatus",
];

export const CAMPAIGN_STATUS_FILTER_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Archived", label: "Archived" },
];

export function getCampaignStatusPillStyle(status) {
  const key = String(status ?? "").trim().toLowerCase();
  if (key === "active") return { background: "#dcfce7", color: "#166534" };
  if (key === "archived") return { background: "#f1f5f9", color: "#475569" };
  return { background: "#e2e8f0", color: "#334155" };
}

export function matchesCampaignStatusFilter(row, selectedLabels) {
  if (!Array.isArray(selectedLabels) || selectedLabels.length === 0) return true;
  const status = String(row?.CampaignStatus ?? "").trim();
  return selectedLabels.some(
    (selected) =>
      String(selected).trim().toLowerCase() === status.toLowerCase(),
  );
}

/** Matches legacy PM Feasibility List columns. */
export const FEASIBILITY_FIELD_OPTIONS = [
  { key: "ParticipantName", label: "Feasibility List" },
  { key: "ParticipantSize", label: "Size" },
  { key: "CreatedDate", label: "Created Date time" },
  { key: "LastEditDateTime", label: "Last Edited" },
  { key: "Status", label: "Status" },
  { key: PM_ACTIONS_COLUMN, label: "Action" },
];

export const FEASIBILITY_DATE_COLUMNS = ["CreatedDate", "LastEditDateTime"];

export const FEASIBILITY_COLUMN_DATE_PRESETS = [
  "Last 30 Days",
  "Last 90 Days",
  "This Year",
  "All Time",
  "Custom",
];

export function getFeasibilityCreatedValue(row) {
  return (
    row?.CreatedDate ||
    row?.CreatedDateTime ||
    row?.createdDate ||
    ""
  );
}

export function getFeasibilityLastEditedValue(row) {
  return (
    row?.LastEditDateTime ||
    row?.LastEdit ||
    row?.lastEditDateTime ||
    ""
  );
}

export const DEFAULT_FEASIBILITY_COLUMNS = [
  "ParticipantName",
  "ParticipantSize",
  "CreatedDate",
  "LastEditDateTime",
  "Status",
  PM_ACTIONS_COLUMN,
];

export function isFeasibilityArchived(row) {
  const flag = String(row?.IsArchive ?? row?.isArchive ?? "").trim().toLowerCase();
  return flag === "1" || flag === "true" || flag === "yes";
}

export function getFeasibilityStatusLabel(row) {
  return isFeasibilityArchived(row) ? "Archived" : "Active";
}

export const PM_FILTERS_INITIAL = {
  genders: [],
  statuses: [],
  volunteerDb: "",
  searchText: "",
};

export { getVolunteerRowId, getVolunteerDisplayName };

export function volStatusLabel(row) {
  return String(row?.VolStatus ?? row?.volStatus ?? "").trim() || "—";
}

export function volStatusToCode(statusLabel) {
  if (statusLabel === 0 || statusLabel === 1 || statusLabel === 2) {
    return statusLabel;
  }
  const text = String(statusLabel ?? "").trim().toLowerCase();
  if (text === "0" || text === "active") return 0;
  if (text === "1" || text === "inactive" || text === "in-active") return 1;
  if (text === "2" || text === "archived" || text === "removed") return 2;
  const asNum = Number(text);
  if (asNum === 0 || asNum === 1 || asNum === 2) return asNum;
  return 0;
}

export function isVolunteerArchived(row) {
  const status = volStatusLabel(row).toLowerCase();
  return status === "archived" || status === "removed";
}

/** Inactive or Removed/Archived — profile is view-only (no edits). */
export function isVolunteerProfileReadOnly(row) {
  const code = volStatusToCode(row?.VolStatus);
  return code === 1 || code === 2;
}

export function matchParticipantTextSearch(row, query) {
  const q = String(query ?? "").trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    getVolunteerDisplayName(row),
    row?.VolunteerNo,
    row?.EmailId,
    row?.ContactNumber,
    row?.Gender,
    row?.DOB,
  ]
    .map((v) => String(v ?? "").toLowerCase())
    .join(" ");
  return haystack.includes(q);
}

export function applyParticipantFilters(rows, filters) {
  if (!filters) return rows || [];
  return (rows || []).filter((row) => {
    if (filters.genders?.length) {
      const gender = String(row?.Gender ?? "").trim();
      if (!filters.genders.includes(gender)) return false;
    }
    if (filters.statuses?.length) {
      const status = volStatusLabel(row);
      if (!filters.statuses.includes(status)) return false;
    }
    if (filters.volunteerDb) {
      const wanted = getParticipantDbLabel(filters.volunteerDb);
      const actual = getParticipantDbLabel(row?.IsUseVolunteerDB);
      if (actual !== wanted) return false;
    }
    if (!matchParticipantTextSearch(row, filters.searchText)) return false;
    return true;
  });
}

export function buildGenderFilterOptions(rows) {
  const set = new Set();
  for (const row of rows || []) {
    const g = String(row?.Gender ?? "").trim();
    if (g) set.add(g);
  }
  return [...set]
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ value, label: value }));
}

export function buildStatusFilterOptions(rows) {
  const set = new Set();
  for (const row of rows || []) {
    const s = volStatusLabel(row);
    if (s && s !== "—") set.add(s);
  }
  return [...set].map((value) => ({ value, label: value }));
}

export function matchTextColumnSearch(row, field, query) {
  const q = String(query ?? "").trim().toLowerCase();
  if (!q) return true;

  if (field === PM_PARTICIPANT_COLUMN || field === PM_ACTIONS_COLUMN) {
    if (field === PM_ACTIONS_COLUMN) return true;
    return matchParticipantTextSearch(row, q);
  }

  if (field === PM_CONTACT_COLUMN) {
    const haystack = [row?.EmailId, row?.ContactNumber]
      .map((v) => String(v ?? "").toLowerCase())
      .join(" ");
    return haystack.includes(q);
  }

  if (field === "DOB") {
    const formatted = formatParticipantDate(row?.DOB).toLowerCase();
    const age = getParticipantAgeYears(row?.DOB);
    const ageText = age == null ? "" : String(age);
    return (
      formatted.includes(q) ||
      ageText.includes(q) ||
      String(row?.DOB ?? "")
        .toLowerCase()
        .includes(q)
    );
  }

  if (field === "IsUseVolunteerDB") {
    const label = getParticipantDbLabel(row?.IsUseVolunteerDB).toLowerCase();
    const raw = String(row?.IsUseVolunteerDB ?? "").toLowerCase();
    return label.includes(q) || raw.includes(q);
  }

  return String(row?.[field] ?? "")
    .toLowerCase()
    .includes(q);
}

export function formatParticipantDate(value) {
  const text = String(value ?? "").trim();
  if (!text || text.toUpperCase() === "N/A") return "—";
  const parsed = utils.parseDateInput(text);
  if (!parsed) return text;
  return parsed.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** Whole years from DOB; null when DOB is missing/invalid. */
export function getParticipantAgeYears(value) {
  const parsed = utils.parseDateInput(value);
  if (!parsed) return null;
  const today = new Date();
  let age = today.getFullYear() - parsed.getFullYear();
  const monthDiff = today.getMonth() - parsed.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < parsed.getDate())) {
    age -= 1;
  }
  return age >= 0 && age < 150 ? age : null;
}

export function getParticipantProfilePictureUrl(row) {
  return String(
    row?.ProfilePictureUrl ??
      row?.profilePictureUrl ??
      row?.ProfileImage ??
      row?.profileImage ??
      "",
  ).trim();
}

/**
 * Numeric / short id used in legacy barcode/QR ("Vol {id}").
 * VolunteerNo is often like "VOL 12345".
 */
export function getVolunteerNoCode(row) {
  const raw = String(row?.VolunteerNo ?? "").trim();
  if (!raw) return "";
  const parts = raw.split(/\s+/);
  return parts.length > 1 ? parts[parts.length - 1] : raw;
}

/** Payload string encoded into the participant QR (matches legacy Volunteers.js). */
export function buildVolunteerQrPayload(row) {
  const code = getVolunteerNoCode(row);
  const first =
    String(row?.FirstName ?? row?.ForeName ?? "").trim();
  const last = String(row?.LastName ?? row?.SurName ?? "").trim();
  const dob = String(row?.DOB ?? "").trim();
  return `{
  ParticipantId:'Vol ${code}',
  FirstName:'${first}',
  LastName: '${last}',
  DOB:'${dob}'
}`;
}

export function buildVolunteerQrImageUrl(row, size = 220) {
  const data = buildVolunteerQrPayload(row);
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(data)}`;
}

/** Display id for labels, e.g. "VOL 215". */
export function getVolunteerLabelId(row) {
  const raw = String(row?.VolunteerNo ?? "").trim();
  if (raw) return raw;
  const code = getVolunteerNoCode(row);
  return code ? `VOL ${code}` : "—";
}

/** Code128 barcode image for check-in label (legacy: Vol {id}). */
export function buildVolunteerBarcodeImageUrl(row, scale = 3) {
  const labelId = getVolunteerLabelId(row);
  const code = getVolunteerNoCode(row);
  const text = labelId !== "—" ? labelId : code ? `Vol ${code}` : "";
  if (!text) return "";
  return `https://bwipjs-api.metafloor.com/?bcid=code128&text=${encodeURIComponent(text)}&scale=${scale}&height=12&includetext=false&backgroundcolor=FFFFFF`;
}

export function getFeasibilityRowId(row) {
  return String(row?.ParticipantId ?? row?.participantId ?? "").trim();
}

export function getTemplateRowId(row) {
  return String(row?.TemplateId ?? row?.templateId ?? "").trim();
}
